// Pure channel-view logic — unit-tested, no Supabase/DOM (the reception
// logic.ts pattern). senderLabel lives in @lemontaigu/staff (shared with the
// S2 analyzer) and is re-exported for the components/tests.
import { todayIsoParis, type StaffMessage, type Team } from '@lemontaigu/staff';

export { senderLabel } from '@lemontaigu/staff';

/**
 * Calendar day key ('2026-07-12') from a KEY-derived Date (e.g. `new
 * Date('${key}T12:00:00')` inside shiftDay) — noon-anchored arithmetic on an
 * already-local synthetic date, timezone-agnostic by construction. NEVER call
 * this on a live instant (`new Date()` or a real timestamp like sentAt /
 * clock_in) — that depends on the RUNTIME's configured timezone, which is not
 * guaranteed to be Europe/Paris (misconfigured device, CI, browser override).
 * Use `todayIsoParis` for that (it takes any Date, explicit Europe/Paris).
 */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** [start, end) ISO instants of a local calendar day, for the sent_at range query. */
export function dayRange(key: string): { fromIso: string; toIso: string } {
  const start = new Date(`${key}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { fromIso: start.toISOString(), toIso: end.toISOString() };
}

export function shiftDay(key: string, delta: number): string {
  const d = new Date(`${key}T12:00:00`); // noon dodges DST edges
  d.setDate(d.getDate() + delta);
  return dayKey(d);
}

/** Fold a Realtime insert into the loaded window: dedupe by id, keep sent order. */
export function mergeMessage(messages: StaffMessage[], incoming: StaffMessage): StaffMessage[] {
  if (messages.some((m) => m.id === incoming.id)) return messages;
  const next = [...messages, incoming];
  next.sort((a, b) => (a.sentAt < b.sentAt ? -1 : a.sentAt > b.sentAt ? 1 : a.id - b.id));
  return next;
}

/** True when a Realtime insert belongs to the currently displayed Europe/Paris day. */
export function belongsToDay(msg: StaffMessage, key: string): boolean {
  return todayIsoParis(new Date(msg.sentAt)) === key;
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export function fmtDayLabel(key: string): string {
  return new Date(`${key}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

// ── Pointages (S3) ───────────────────────────────────────────────────────────

/** First day of the month a day key belongs to ('2026-07-13' → '2026-07-01'). */
export function monthStartKey(key: string): string {
  return `${key.slice(0, 8)}01`;
}

/** ISO instant → value for an <input type="datetime-local"> (local wall time). */
export function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO instant, or null when empty/invalid. */
export function fromDatetimeLocal(value: string): string | null {
  if (value.trim() === '') return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** Decimal hours, French presentation ('7,5 h'; '0,02 h'). */
export function fmtHours(hours: number): string {
  return `${hours.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;
}

// ── Team labels (Sur place, Présence) ────────────────────────────────────────
// The roster EDITOR (EmployeeDraft + its validation) moved to apps/admin with
// the Équipe tab (APPS-REORG R2) — apps/admin/src/logic.ts owns that now.

/** French label for a team code (or « — » when unset). */
export function teamLabel(team: '' | Team | null): string {
  switch (team) {
    case 'menage':
      return 'Ménage';
    case 'receptionSalle':
      return 'Réception / Salle';
    case 'cuisine':
      return 'Cuisine';
    default:
      return '—';
  }
}

/**
 * A shift as an employee thinks of it — « le 30, de 22:00 à 02:00 » — to the two
 * instants the request needs. A departure at or before the arrival is the NEXT
 * day (a night shift), so nobody has to type a second date. Null when either
 * time is missing or unparseable.
 */
export function composeShift(
  day: string,
  start: string,
  end: string,
): { clockInIso: string; clockOutIso: string } | null {
  const inMs = Date.parse(`${day}T${start}`);
  let outMs = Date.parse(`${day}T${end}`);
  if (!Number.isFinite(inMs) || !Number.isFinite(outMs)) return null;
  if (outMs <= inMs) {
    const next = new Date(outMs);
    next.setDate(next.getDate() + 1);
    outMs = next.getTime();
  }
  return { clockInIso: new Date(inMs).toISOString(), clockOutIso: new Date(outMs).toISOString() };
}

/** HH:MM of an instant, in the browser's (Paris) time — the value of an <input type="time">. */
export function toTimeInput(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}
